/**
 * Robust clipboard write with a legacy-WebView fallback.
 *
 * ``navigator.clipboard.writeText`` is the modern API but it fails
 * silently in several environments we ship to:
 *
 *   - older Android WebView (Chromium < 70-ish): API is undefined.
 *   - iOS in-app webviews (TG client on older iOS): the Promise
 *     rejects with a Permission error even from a synchronous user
 *     gesture handler in some TG-client versions.
 *   - any browser when ``Permissions-Policy: clipboard-write=()``
 *     is set on the embedding page.
 *
 * The legacy ``document.execCommand('copy')`` route works in those
 * environments but only when called synchronously from a user
 * gesture and against a real ``<textarea>`` already in the DOM with
 * an active selection. We try the modern path first; on rejection
 * (or absence) we fall back.
 *
 * Returns ``true`` on success, ``false`` on full failure — the
 * caller is expected to surface an error to the user (haptic / toast)
 * rather than silently swallowing the result, which is the bug the
 * previous ``catch { /* ignore *\/ }`` blocks introduced.
 */
export async function copyText(text: string): Promise<boolean> {
  // Modern path.
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function'
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to legacy path.
  }

  // Legacy path: temporary textarea + execCommand. Must run
  // synchronously within the user-gesture stack on iOS.
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    // Off-screen but selectable — visibility:hidden / display:none
    // both break selection in some WebViews, so we use a fixed
    // off-screen position instead.
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.width = '1px';
    ta.style.height = '1px';
    ta.style.padding = '0';
    ta.style.border = 'none';
    ta.style.opacity = '0';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
